import { Callout, Level2InnerPageLayout, noOp, type PageAction } from "@octopusdeploy/design-system-components";
import { logger } from "@octopusdeploy/logging";
import { useAggregateAPIOperationStatus, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ChannelResource, EnvironmentResource, FeedResource, GetTeamsLookupBffResponseTeam, GetTemplatedProjectParametersResponse, GitRefResource, ProjectResource, ProjectSummaryResource, PropertyValueResource, Repository, ValidateGitRefV2Response, VariableSetResource, WorkerPoolResource, PlatformHubVersionControlSettingsResource, } from "@octopusdeploy/octopus-server-client";
import { HasVariablesInGit, mapFromEnvironmentResourceToEnvironmentV2Resource, Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { omit } from "lodash";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { Action, useProjectScopedAnalyticActionDispatch } from "~/analytics/Analytics";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PageHeaderBranchSelector } from "~/areas/projects/components/BranchSelector/PageHeaderBranchSelector";
import { toTemplatedProjectProcessIdentifier } from "~/areas/projects/components/Process/types";
import { type LoadedLibraryVariableSets, loadLibraryVariableSetVariables } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import { TemplatedProjectParameterValues } from "~/areas/projects/components/Variables/TemplateParameters/TemplatedProjectParameterValues";
import { VcsError } from "~/areas/projects/components/VersionControl/VcsError";
import { type ProjectContextProps, useProjectContext } from "~/areas/projects/context";
import { repository } from "~/clientInstance";
import { ScriptActionContext } from "~/components/Actions/script/ScriptActionContext";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Form, useForm } from "~/components/form/Form/Form";
interface TemplatedProjectParametersModel {
    readonly parameterValues: Record<string, PropertyValueResource | undefined>;
}
export interface TemplatedProjectParametersLoaderData {
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    initialValidationResult: ValidateGitRefV2Response | undefined;
    templateParameters: GetTemplatedProjectParametersResponse;
    environments: EnvironmentResource[];
    workerPools: WorkerPoolResource[];
    machineRoles: string[];
    teams: GetTeamsLookupBffResponseTeam[];
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    feeds: FeedResource[];
    projectVariableSet: VariableSetResource;
    sensitiveVariables: VariableSetResource | null;
    libraryVariableSets: LoadedLibraryVariableSets[];
}
export interface TemplatedProjectParametersPageProps {
    loaderData: TemplatedProjectParametersLoaderData;
}
export async function templatedProjectParametersPageLoader(repository: Repository, projectContext: Promise<ProjectContextProps>): Promise<TemplatedProjectParametersLoaderData> {
    const persistenceSettingsPromise = repository.PlatformHub.getVersionControlSettings();
    const resolvedProjectContext = await projectContext;
    const project = resolvedProjectContext.state.model;
    const gitRef = resolvedProjectContext.state.gitRef;
    const projectContextRepository = resolvedProjectContext.state.projectContextRepository;
    const isGitProject = HasVariablesInGit(project.PersistenceSettings);
    // Fire all API calls in parallel
    const templateParametersPromise = isGitProject && gitRef ? repository.TemplatedProjects.getTemplatedProjectParametersInGit(project.Id, gitRef.CanonicalName) : repository.TemplatedProjects.getTemplatedProjectParameters(project.Id);
    const environmentsPromise = repository.Environments.all();
    const workerPoolsPromise = repository.WorkerPools.list();
    const machineRolesPromise = repository.MachineRoles.all();
    const teamsPromise = repository.Teams.lookupBff();
    const channelsPromise = repository.Channels.listFromProject(project);
    const projectsPromise = repository.Projects.summaries();
    const feedsPromise = repository.Feeds.all();
    const projectVariableSetPromise = projectContextRepository.Variables.get();
    const sensitiveVariablesPromise = isGitProject ? repository.ProjectVariables.getSensitive(project.Id, project.PersistenceSettings) : Promise.resolve(null);
    const libraryVariableSetsPromise = project.IncludedLibraryVariableSetIds?.length ? loadLibraryVariableSetVariables(project.IncludedLibraryVariableSetIds) : Promise.resolve<LoadedLibraryVariableSets[]>([]);
    const persistenceSettings = await persistenceSettingsPromise;
    let initialValidationResult: ValidateGitRefV2Response | undefined = undefined;
    if (persistenceSettings.Url) {
        const platformHubGitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
        initialValidationResult = await repository.PlatformHub.validateGitRef(platformHubGitRef);
    }
    const workerPoolsResult = await workerPoolsPromise;
    const channelsResult = await channelsPromise;
    return {
        persistenceSettings,
        initialValidationResult,
        templateParameters: await templateParametersPromise,
        environments: await environmentsPromise,
        workerPools: workerPoolsResult.Items,
        machineRoles: await machineRolesPromise,
        teams: await teamsPromise,
        channels: channelsResult.Items,
        projects: await projectsPromise,
        feeds: await feedsPromise,
        projectVariableSet: await projectVariableSetPromise,
        sensitiveVariables: await sensitiveVariablesPromise,
        libraryVariableSets: await libraryVariableSetsPromise,
    };
}
export function TemplatedProjectParameters({ loaderData }: TemplatedProjectParametersPageProps) {
    const projectContext = useProjectContext();
    const { model: project, gitRef, gitRefValidationError } = projectContext.state;
    const spaceNavigation = useSpaceAwareNavigation();
    const { search: urlSearch } = useLocation();
    const navigateToGitRef = (gitRef: string) => {
        const newUrl = links.branchTemplateParametersPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, branchName: gitRef }).resolveWithSpaceId(project.SpaceId);
        const fullUrl = `${newUrl}${urlSearch}`;
        spaceNavigation.navigate(fullUrl);
    };
    const { result: feedsCollection, refetch: refetchFeeds } = useQuery(() => repository.Feeds.all(), [], "Get feeds", { initialResult: loaderData.feeds });
    const { result: projectVariableSet, refetch: refetchProjectVariables } = useQuery(() => projectContext.state.projectContextRepository.Variables.get(), [projectContext.state.projectContextRepository.Variables], "Get project variables", {
        initialResult: loaderData.projectVariableSet,
    });
    const mergedProjectVariableSet = React.useMemo(() => {
        if (!loaderData.sensitiveVariables) {
            return projectVariableSet;
        }
        return {
            ...projectVariableSet,
            Variables: (projectVariableSet.Variables ?? []).concat(loaderData.sensitiveVariables.Variables),
        };
    }, [projectVariableSet, loaderData.sensitiveVariables]);
    const platformHubGitRef = React.useMemo(() => {
        if (!loaderData.persistenceSettings.Url) {
            return undefined;
        }
        return lastAccessedPlatformHubGitRef.get(loaderData.persistenceSettings);
    }, [loaderData.persistenceSettings]);
    if (gitRefValidationError && gitRefValidationError.Type !== "Success") {
        return (<Level2InnerPageLayout header={{
                title: "Template Values",
                contextSelector: <PageHeaderBranchSelector project={project} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/>,
            }} busy={false} errors={[]} fullWidth={false}>
                <VcsError project={project} error={gitRefValidationError} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/>
            </Level2InnerPageLayout>);
    }
    if (!loaderData.persistenceSettings.Url) {
        return (<Callout title="Platform Hub Not Configured" type="information">
                <div>Platform Hub version control settings must be configured to use template parameters.</div>
            </Callout>);
    }
    if (!platformHubGitRef) {
        return (<Callout title="Git Reference Not Available" type="danger">
                <div>Unable to determine the git reference for Platform Hub. Please check your Platform Hub configuration.</div>
            </Callout>);
    }
    const { templateParameters } = loaderData;
    if (!templateParameters.Parameters || templateParameters.Parameters.length < 1) {
        return (<Callout title="No Template Parameters" type="information">
                <div>This project does not have any template parameters configured.</div>
            </Callout>);
    }
    const environments = loaderData.environments.map(mapFromEnvironmentResourceToEnvironmentV2Resource);
    const parametersSignature = templateParameters.ParameterValues ? JSON.stringify(templateParameters.ParameterValues) : "empty";
    const formKey = `${project.Id}-${parametersSignature}`;
    return (<TemplatedProjectParametersForm key={formKey} templateParameters={templateParameters} project={project} gitRef={gitRef} navigateToGitRef={navigateToGitRef} environments={environments} workerPools={loaderData.workerPools} machineRoles={loaderData.machineRoles} teams={loaderData.teams} channels={loaderData.channels} projects={loaderData.projects} feeds={feedsCollection} mergedProjectVariableSet={mergedProjectVariableSet} loadedLibraryVariableSets={loaderData.libraryVariableSets} platformHubGitRef={platformHubGitRef} refetchProjectVariables={refetchProjectVariables} refetchFeeds={refetchFeeds}/>);
}
function TemplatedProjectParametersForm({ templateParameters, project, gitRef, navigateToGitRef, environments, workerPools, machineRoles, teams, channels, projects, feeds, mergedProjectVariableSet, loadedLibraryVariableSets, platformHubGitRef, refetchProjectVariables, refetchFeeds, }: {
    templateParameters: GetTemplatedProjectParametersResponse;
    project: ProjectResource;
    gitRef: GitRefResource | undefined;
    navigateToGitRef: (gitRef: string) => void;
    environments: ReturnType<typeof mapFromEnvironmentResourceToEnvironmentV2Resource>[];
    workerPools: WorkerPoolResource[];
    machineRoles: string[];
    teams: GetTeamsLookupBffResponseTeam[];
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    feeds: FeedResource[];
    mergedProjectVariableSet: VariableSetResource;
    loadedLibraryVariableSets: LoadedLibraryVariableSets[];
    platformHubGitRef: string | undefined;
    refetchProjectVariables: () => void;
    refetchFeeds: () => void;
}) {
    const projectId = project.Id;
    const spaceId = project.SpaceId;
    const { isInProgress, errors } = useAggregateAPIOperationStatus();
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectId);
    const { result: deploymentProcess } = useQuery(async () => await repository.TemplatedProjects.getTemplatedProjectDeploymentProcess(projectId), [projectId], "Get deployment process for create release button");
    const loadVariablesKey = React.useMemo(() => {
        if (!project.ProjectTemplateDetails) {
            return "";
        }
        const processIdentifier = toTemplatedProjectProcessIdentifier(project, project.ProjectTemplateDetails);
        const gitRefName = gitRef?.CanonicalName;
        return [processIdentifier.projectId, processIdentifier.projectTemplateSlug, processIdentifier.version, gitRefName].join("_");
    }, [project, gitRef]);
    const scriptActionContext = React.useMemo(() => ({
        loadVariables: async () => {
            if (!project.ProjectTemplateDetails) {
                return [];
            }
            try {
                const processIdentifier = toTemplatedProjectProcessIdentifier(project, project.ProjectTemplateDetails);
                const gitRefName = gitRef?.CanonicalName;
                return await repository.Variables.getNamesForDeploymentProcess(processIdentifier.projectId, null, gitRefName);
            }
            catch (error) {
                logger.error(error, "Failed to load variables for templated project");
                return [];
            }
        },
        key: loadVariablesKey,
    }), [project, gitRef, loadVariablesKey]);
    const { model, setModel, createSaveAction, formProps } = useForm({
        formName: "Template Parameters",
        initialModel: { parameterValues: templateParameters.ParameterValues || {} },
        onSubmit: async (repository, model: TemplatedProjectParametersModel) => {
            await saveTemplateParameters(model.parameterValues);
            return model;
        },
        updateFormModelAfterSubmit: (model) => model,
        submitPermission: {
            permission: Permission.ProjectEdit,
            project: project.Id,
        },
        snackbarSaveText: "Template parameters saved",
    });
    const handleParameterValueChanged = (parameterName: string, value: PropertyValueResource | undefined) => {
        setModel((prev: TemplatedProjectParametersModel) => {
            if (!value) {
                return {
                    ...prev,
                    parameterValues: omit(prev.parameterValues, [parameterName]),
                };
            }
            return {
                ...prev,
                parameterValues: {
                    ...prev.parameterValues,
                    [parameterName]: value,
                },
            };
        });
    };
    const saveTemplateParameters = async (parameterValues: Record<string, PropertyValueResource | undefined>) => {
        if (!projectId || !spaceId || !templateParameters?.Parameters) {
            throw new Error("Missing project ID, space ID, or template parameters");
        }
        const valuesToSave: Record<string, PropertyValueResource> = {};
        for (const [key, value] of Object.entries(parameterValues)) {
            if (value !== undefined) {
                valuesToSave[key] = value;
            }
        }
        if (HasVariablesInGit(project.PersistenceSettings) && gitRef) {
            await repository.TemplatedProjects.saveTemplateParameterValuesInGit({
                spaceId,
                projectId,
                gitRef: gitRef.CanonicalName,
                parameterValues: valuesToSave,
            });
        }
        else {
            await repository.TemplatedProjects.saveTemplateParameterValues({
                spaceId,
                projectId,
                parameterValues: valuesToSave,
            });
        }
    };
    // Create page actions including create release button
    const pageActions: PageAction[] = [];
    const hasSteps = deploymentProcess?.Steps && deploymentProcess.Steps.length > 0;
    const createReleaseIsAllowed = hasSteps || project.IsVersionControlled;
    if (project && createReleaseIsAllowed) {
        const createReleaseButton: PageAction = {
            type: "navigate",
            buttonType: "secondary",
            label: "Create Release",
            path: links.createReleasePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }),
            onClick: () => dispatchAction("Create a release", { resource: "Create Release", action: Action.Add }),
            hasPermissions: isAllowed({ permission: Permission.ReleaseCreate, project: project.Id, tenant: "*" }),
        };
        pageActions.push(createReleaseButton);
    }
    return (
    // The insert variable button component used across deployment processes & parameter values requires the ScriptActionContext
    <ScriptActionContext.Provider value={scriptActionContext}>
            <Level2InnerPageLayout header={{
            title: "Template Values",
            primaryAction: createSaveAction(),
            pageActions: pageActions,
            contextSelector: HasVariablesInGit(project.PersistenceSettings) ? <PageHeaderBranchSelector project={project} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/> : undefined,
        }} busy={isInProgress} errors={errors.map(createErrorInfoFromError)} fullWidth={false}>
                <Form {...formProps}>
                    <TemplatedProjectParameterValues doBusyTask={async (task) => {
            await task();
            return true;
        }} onParameterValueChanged={handleParameterValueChanged} parameterValues={model.parameterValues} parameters={templateParameters.Parameters} gitRefResource={gitRef} projectVariableSet={mergedProjectVariableSet} libraryVariableSets={loadedLibraryVariableSets || []} environments={environments} analyticsStepEditorDispatch={noOp} availableWorkerPools={workerPools} machineRoles={machineRoles} teams={teams} isTenanted={project.TenantedDeploymentMode !== "Untenanted"} channels={channels} projects={projects} feeds={feeds} refreshVariables={async () => {
            refetchProjectVariables();
        }} refreshFeeds={async () => {
            refetchFeeds();
        }} platformHubDefaultGitRef={platformHubGitRef || ""}/>
                </Form>
            </Level2InnerPageLayout>
        </ScriptActionContext.Provider>);
}
